import { motion } from "framer-motion";
import {
  Store,
  ShoppingBag,
  Zap,
  Layers,
  TrendingUp,
  ArrowUpRight,
} from "lucide-react";

const services = [
  {
    icon: Store,
    title: "Amazon Store Setup & Optimization",
    desc: "Seller account, brand registry, category approvals and a storefront built to win the buy box from day one.",
    tags: ["Seller onboarding", "Brand registry", "A+ content", "Buy-box ops"],
    span: "lg:col-span-4",
  },
  {
    icon: ShoppingBag,
    title: "Shopify Store Setup & Design",
    desc: "Conversion-first D2C storefronts — theme, UX, payments, shipping and the app stack, launch-ready in weeks.",
    tags: ["Store design", "Payments & shipping", "App stack"],
    span: "lg:col-span-2",
  },
  {
    icon: Zap,
    title: "Quick Commerce Onboarding",
    desc: "Blinkit, Zepto and Instamart — listings, GTINs, PO management and dark-store availability, handled end-to-end.",
    tags: ["Blinkit", "Zepto", "Instamart", "PO management"],
    span: "lg:col-span-2",
  },
  {
    icon: Layers,
    title: "Catalog, Listings & Content",
    desc: "Keyword-rich titles, bullet-point copy that converts, image direction and A+ pages that make your brand look big.",
    tags: ["SEO copy", "Image direction", "Keyword research"],
    span: "lg:col-span-2",
  },
  {
    icon: TrendingUp,
    title: "Ads & Growth",
    desc: "An optional add-on once you're live — Amazon PPC, q-com banners and D2C performance campaigns tuned for ROAS.",
    tags: ["Amazon PPC", "Q-com banners", "Add-on service"],
    span: "lg:col-span-2",
  },
];

export const Services = () => {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-28 lg:py-40 bg-midnight"
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <div className="flex items-start justify-between mb-16 lg:mb-24">
          <div>
            <motion.span
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-xs font-bold uppercase tracking-[0.25em] text-coal/40"
            >
              01 — Services
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="font-display font-900 tracking-tight leading-none text-4xl sm:text-5xl lg:text-6xl mt-4 max-w-2xl"
            >
              Everything between you and{" "}
              <span className="font-accent italic font-medium text-terracotta">
                sold out.
              </span>
            </motion.h2>
          </div>
          <motion.span
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="hidden lg:block font-display font-900 text-8xl text-outline-gold select-none"
          >
            01
          </motion.span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-6 gap-px bg-coal/10 border border-coal/10">
          {services.map((s, i) => (
            <motion.article
              key={s.title}
              data-testid={`service-card-${i}`}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{
                duration: 0.8,
                delay: i * 0.08,
                ease: [0.16, 1, 0.3, 1],
              }}
              whileHover={{ y: -4 }}
              className={`group relative bg-midnight p-8 lg:p-12 hover:bg-ink transition-colors duration-500 ${s.span}`}
            >
              <div className="absolute top-0 left-0 h-[3px] w-0 bg-terracotta group-hover:w-full transition-[width] duration-500" />
              <div className="flex items-start justify-between">
                <s.icon
                  size={36}
                  strokeWidth={1.5}
                  className="text-gold group-hover:text-terracotta transition-colors duration-500"
                />
                <ArrowUpRight
                  size={20}
                  className="text-coal/20 group-hover:text-terracotta group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-500"
                />
              </div>
              <h3 className="font-display font-800 text-2xl sm:text-3xl tracking-tight mt-10 leading-tight">
                {s.title}
              </h3>
              <p className="mt-4 text-sm sm:text-base leading-relaxed text-coal/60">
                {s.desc}
              </p>
              <div className="mt-8 flex flex-wrap gap-2">
                {s.tags.map((t) => (
                  <span
                    key={t}
                    className="text-[11px] font-bold uppercase tracking-[0.15em] text-coal/40 border border-coal/15 px-3 py-1.5 group-hover:border-terracotta/40 group-hover:text-coal/60 transition-colors duration-500"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </motion.article>
          ))}

          <motion.a
            href="#contact"
            data-testid="services-cta-tile"
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => {
              e.preventDefault();
              document
                .querySelector("#contact")
                ?.scrollIntoView({ behavior: "smooth" });
            }}
            className="group lg:col-span-6 bg-terracotta p-8 lg:p-12 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:bg-gold transition-colors duration-500"
          >
            <span className="font-display font-900 text-2xl sm:text-4xl tracking-tight text-night">
              Not sure where to start? We'll map it for you — free.
            </span>
            <ArrowUpRight
              size={40}
              strokeWidth={2}
              className="text-night group-hover:translate-x-2 group-hover:-translate-y-2 transition-transform duration-500 shrink-0"
            />
          </motion.a>
        </div>
      </div>
    </section>
  );
};
